import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Users, MessagesSquare, ChevronDown, Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { EmailAlertStatus } from "../components/EmailAlertStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("uma_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [chats, setChats] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (tok) => {
    try {
      const h = { headers: { Authorization: `Bearer ${tok}` } };
      const [l, c] = await Promise.all([
        axios.get(`${API}/leads`, h),
        axios.get(`${API}/admin/chats`, h),
      ]);
      setLeads(l.data.leads);
      setChats(c.data.sessions);
    } catch {
      sessionStorage.removeItem("uma_admin_token");
      setToken("");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("uma_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Wrong password. Please try again.");
    }
    setLoading(false);
  };

  const logout = () => {
    sessionStorage.removeItem("uma_admin_token");
    setToken("");
    setLeads([]);
    setChats([]);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950 px-4">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="glass-card w-full max-w-sm rounded-3xl p-8"
        >
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-solar/15">
            <Lock size={26} className="text-solar-yellow" />
          </div>
          <h1 className="text-center font-display text-2xl font-extrabold text-white">Owner Login</h1>
          <p className="mt-1 text-center text-sm text-slate-400">Uma Enterprises — Leads Dashboard</p>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            data-testid="admin-password-input"
            className="mt-6 w-full rounded-xl border border-white/10 bg-navy-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-solar/60 focus:ring-2 focus:ring-solar/20"
          />
          {error && <p className="mt-2 text-center text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-solar px-6 py-3 text-sm font-bold text-amber-950 transition-colors hover:bg-solar-yellow disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />} Login
          </button>
          <a href="/" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500 transition-colors hover:text-solar-yellow" data-testid="admin-back-link">
            <ArrowLeft size={13} /> Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-6xl" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-white sm:text-3xl">Leads Dashboard</h1>
            <p className="mt-1 text-sm text-slate-400">Enquiries from the website form and Uma AI chatbot</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" disabled={refreshing}
              onClick={async () => { setRefreshing(true); await load(token); setRefreshing(false); }}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:text-eco-bright disabled:opacity-60">
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/" className="rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:text-solar-yellow" data-testid="admin-back-link">
              View Site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2.5 text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/20"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-navy-900 p-5" data-testid="stat-total-leads">
            <Users size={18} className="text-solar-yellow" />
            <div className="mt-3 font-mono text-3xl font-black text-white">{leads.length}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Total Enquiries</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-navy-900 p-5" data-testid="stat-chat-sessions">
            <MessagesSquare size={18} className="text-eco-bright" />
            <div className="mt-3 font-mono text-3xl font-black text-white">{chats.length}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Chat Sessions</div>
          </div>
        </div>

        <h2 className="mt-10 font-display text-lg font-bold text-white">Form Enquiries</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-testid="leads-table">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-navy-900 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3.5">Name</th>
                <th className="px-5 py-3.5">Phone</th>
                <th className="px-5 py-3.5">Service</th>
                <th className="px-5 py-3.5">City</th>
                <th className="px-5 py-3.5">Bill</th>
                <th className="px-5 py-3.5">Date</th>
                <th className="px-5 py-3.5" data-testid="leads-email-alert-heading">Email alert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 bg-navy-950/60">
              {leads.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-500" data-testid="leads-empty">No enquiries yet — they will appear here as soon as someone submits the form.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="text-slate-300 transition-colors hover:bg-navy-900/60">
                  <td className="px-5 py-3.5 font-semibold text-white" data-testid={`lead-name-${l.id}`}>{l.name}</td>
                  <td className="px-5 py-3.5">
                    <a href={`tel:+91${l.phone.replace(/\D/g, "").slice(-10)}`} data-testid={`lead-phone-${l.id}`} className="font-mono text-eco-bright hover:underline">{l.phone}</a>
                  </td>
                  <td className="px-5 py-3.5" data-testid={`lead-service-${l.id}`}>{l.service}</td>
                  <td className="px-5 py-3.5" data-testid={`lead-city-${l.id}`}>{l.city || "—"}</td>
                  <td className="px-5 py-3.5" data-testid={`lead-bill-${l.id}`}>{l.monthly_bill || "—"}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-slate-500" data-testid={`lead-date-${l.id}`}>{new Date(l.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3.5"><EmailAlertStatus alert={l.email_alert} leadId={l.id} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-10 font-display text-lg font-bold text-white">Chatbot Conversations</h2>
        <div className="mt-4 space-y-3" data-testid="chats-list">
          {chats.length === 0 && <p className="rounded-2xl border border-white/10 bg-navy-950/60 px-5 py-8 text-center text-slate-500">No chatbot conversations yet.</p>}
          {chats.map((s) => (
            <div key={s.session_id} className="overflow-hidden rounded-2xl border border-white/10 bg-navy-950/60">
              <button
                onClick={() => setOpenSession(openSession === s.session_id ? null : s.session_id)}
                className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-navy-900/60"
                data-testid={`chat-session-${s.session_id}`}
              >
                <div>
                  <span className="font-mono text-sm text-slate-300">{s.session_id}</span>
                  <span className="ml-3 rounded-full bg-navy-800 px-2.5 py-0.5 text-xs text-slate-400">{s.count} messages</span>
                </div>
                <ChevronDown size={16} className={`text-slate-500 transition-transform duration-300 ${openSession === s.session_id ? "rotate-180" : ""}`} />
              </button>
              {openSession === s.session_id && (
                <div className="space-y-2.5 border-t border-white/10 px-5 py-4">
                  {s.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-solar/15 text-solar-highlight" : "bg-navy-900 text-slate-300"}`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
